import signature from '../assets/brand/uncle-signature.svg';
import type { Department, DepartmentTask, WorkOrder } from './types';
import { PAPER_PRODUCTION_TYPES } from './access';

const shortDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

function normalizeDeptName(name: string): string {
  return name.trim().toUpperCase().replace(/\s+/g, ' ');
}

type Props = {
  order: WorkOrder;
  task: DepartmentTask;
  departments: Department[];
};

export function PaperWorkOrderSheet({ order, task, departments }: Props) {
  const item = order.items.find((entry) => entry.id === task.workOrderItemId);
  const selectedDeptNames = new Set<string>();
  for (const departmentId of item?.departmentIds ?? [task.departmentId]) {
    const department = departments.find((entry) => entry.id === departmentId);
    if (department) selectedDeptNames.add(normalizeDeptName(department.name));
  }
  selectedDeptNames.add(normalizeDeptName(task.departmentName));

  const isSupplier = order.customerType === 'b2b';

  return (
    <div className="paper-work-order" id="paper-work-order-print">
      <div className="paper-work-order-brand">
        <img src={signature} alt="Uncle Advertisers" />
        <span className="paper-work-order-brand-meta">
          <span>Job ticket</span>
          <strong>{order.orderNumber}</strong>
        </span>
      </div>
      <header className="paper-work-order-header">
        <h1 className="paper-work-order-title">ORDER FORM</h1>
        <div className="paper-work-order-header-meta">
          <div className="paper-work-order-field paper-work-order-field--inline">
            <span className="paper-work-order-label">ORDER NO. :</span>
            <span className="paper-work-order-value">{order.orderNumber}</span>
          </div>
          <div className="paper-work-order-field paper-work-order-field--inline">
            <span className="paper-work-order-label">INVOICE NO. :</span>
            <span className="paper-work-order-value">{order.invoice?.number ?? ''}</span>
          </div>
        </div>
      </header>

      <div className="paper-work-order-checkrow">
        <PaperCheckbox label="NEW CUSTOMER" checked={false} />
        <PaperCheckbox label="SUPPLIER" checked={isSupplier} />
      </div>

      <section className="paper-work-order-box paper-work-order-customer">
        <div className="paper-work-order-customer-grid">
          <div className="paper-work-order-field">
            <span className="paper-work-order-label">NAME:</span>
            <span className="paper-work-order-value">{order.customerName}</span>
          </div>
          <div className="paper-work-order-field">
            <span className="paper-work-order-label">DATE :</span>
            <span className="paper-work-order-value">{shortDate(order.orderDate)}</span>
          </div>
          <div className="paper-work-order-field paper-work-order-field--wide">
            <span className="paper-work-order-label">ADDRESS:</span>
            <span className="paper-work-order-value">{order.customerAddress || order.deliveryAddress || ''}</span>
          </div>
          <div className="paper-work-order-field">
            <span className="paper-work-order-label">PHONE:</span>
            <span className="paper-work-order-value">{order.customerPhone || ''}</span>
          </div>
        </div>
      </section>

      <section className="paper-work-order-box paper-work-order-types">
        <div className="paper-work-order-type-grid">
          {PAPER_PRODUCTION_TYPES.map((type) => (
            <PaperCheckbox
              key={type}
              label={type}
              checked={selectedDeptNames.has(type)}
            />
          ))}
        </div>
      </section>

      <section className="paper-work-order-box paper-work-order-details">
        <div className="paper-work-order-section-title">DETAILS :</div>
        <div className="paper-work-order-details-body">{task.itemDescription}</div>
      </section>

      <section className="paper-work-order-box paper-work-order-requirements">
        <div className="paper-work-order-section-title">ANY ORDER REQUIREMENT</div>
        <div className="paper-work-order-requirements-grid">
          <RequirementLine label="INSTALLATION / DELIVERY" value={order.installation} />
          <RequirementLine label="REPAIR" value={order.repair} />
        </div>
      </section>

      <section className="paper-work-order-box paper-work-order-delivery">
        <div className="paper-work-order-section-title">DELIVERY DATE &amp; ADDRESS :</div>
        <div className="paper-work-order-delivery-body">
          <div>{shortDate(order.deliveryDate)}</div>
          {order.deliveryAddress && <div>{order.deliveryAddress}</div>}
        </div>
      </section>

      <footer className="paper-work-order-footer">
        <div className="paper-work-order-field paper-work-order-field--grow">
          <span className="paper-work-order-label">NAME OF CONTACT PERSON / GRAPHIC DESIGNER :</span>
          <span className="paper-work-order-value">{order.contactPerson}</span>
        </div>
        <div className="paper-work-order-field paper-work-order-signature">
          <span className="paper-work-order-label">SIGNATURE :</span>
          <span className="paper-work-order-value" />
        </div>
      </footer>
    </div>
  );
}

function PaperCheckbox({ label, checked }: { label: string; checked: boolean }) {
  return (
    <label className="paper-work-order-checkbox">
      <span className="paper-work-order-checkbox-box" aria-hidden>{checked ? '✓' : ''}</span>
      <span>{label}</span>
    </label>
  );
}

function RequirementLine({
  label,
  value,
}: {
  label: string;
  value: WorkOrder['installation'];
}) {
  return (
    <div className="paper-work-order-requirement">
      <div className="paper-work-order-field">
        <span className="paper-work-order-label">{label} :</span>
        <span className="paper-work-order-value">
          {value.required ? value.details || 'Yes' : ''}
        </span>
      </div>
    </div>
  );
}
